import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { productCategories } from '../mockData';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Submit to backend API
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 200) {
        toast.success('Message sent successfully!', {
          description: 'We will contact you within 24 hours at info@univsurgical.com and sharjeel@univsurgical.com'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          category: '',
          message: ''
        });
      }
    } catch (error) {
      // Log error for debugging (remove in production or use proper error tracking service)
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting form:', error);
      }
      toast.error('Failed to send message', {
        description: 'Please try again or contact us directly at info@univsurgical.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-red-600 text-white border-0">Contact Us</Badge>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Get In Touch</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Have questions about our products? Request a quote or reach out to our team
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="space-y-6">
            {/* Contact Cards */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <p className="text-gray-600 text-sm mb-2">Mon-Fri 9am-6pm EST</p>
                    <div className="space-y-1">
                      <div>
                        <span className="text-xs text-gray-500">Canada:</span>
                        <a href="tel:+15877090167" className="text-red-600 hover:text-red-700 font-medium block">
                          +1-587-709-0167
                        </a>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Pakistan:</span>
                        <a href="tel:+923342744344" className="text-red-600 hover:text-red-700 font-medium block">
                          +92 334 2744344
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600 text-sm mb-2">We&apos;ll respond within 24 hours</p>
                    <div className="space-y-1">
                      <a href="mailto:info@univsurgical.com" className="text-red-600 hover:text-red-700 font-medium block">
                        info@univsurgical.com
                      </a>
                      <a href="mailto:sharjeel@univsurgical.com" className="text-red-600 hover:text-red-700 font-medium block">
                        sharjeel@univsurgical.com
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Our Locations</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Pakistan Office</p>
                        <p className="text-sm text-gray-600">
                          Daska Road<br />
                          Sialkot, Punjab<br />
                          Pakistan
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Canada Office</p>
                        <p className="text-sm text-gray-600">
                          12040 95 St NW<br />
                          Edmonton, AB<br />
                          Canada
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                    <div className="text-gray-600 text-sm space-y-1">
                      <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p>Saturday: 10:00 AM - 4:00 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Request a Quote</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name" className="text-gray-700 mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Dr. John Smith"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-gray-700 mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@hospital.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        className="h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-gray-700 mb-2 block">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+1 (234) 567-890"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        className="h-12"
                      />
                    </div>
                  </div>

                  {/* Company & Category */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company" className="text-gray-700 mb-2 block">
                        Company/Hospital
                      </Label>
                      <Input
                        id="company"
                        type="text"
                        placeholder="Memorial Hospital"
                        value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        className="h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="category" className="text-gray-700 mb-2 block">
                        Product Category of Interest
                      </Label>
                      <Select value={formData.category} onValueChange={(value) => handleChange('category', value)}>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Inquiry</SelectItem>
                          {productCategories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="text-gray-700 mb-2 block">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Please provide details about your requirements, quantity needed, delivery location, etc."
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      required
                      rows={6}
                      className="resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-red-600 hover:bg-red-700 text-white h-12"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    Your message will be sent to info@univsurgical.com and sharjeel@univsurgical.com
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-16 bg-gray-50 rounded-2xl p-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">International Orders Welcome</h2>
            <p className="text-lg text-gray-600 mb-6">
              We export to over 50 countries worldwide. Our team can assist with international shipping, 
              customs documentation, and compliance requirements. Contact us to discuss your specific needs.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Badge variant="outline" className="text-sm px-4 py-2">FDA Registered</Badge>
              <Badge variant="outline" className="text-sm px-4 py-2">CE Marked</Badge>
              <Badge variant="outline" className="text-sm px-4 py-2">ISO 13485 Certified</Badge>
              <Badge variant="outline" className="text-sm px-4 py-2">GMP Compliant</Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
